import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward,  MdDevices, MdOutlineSecurity, MdOutlineLocalShipping, MdOutlineVerified } from 'react-icons/md';

export default function AppleLayout({ p }) {
  const T = p.storeTheme || {};
  const buttonColor = T.buttonColor || "#0071e3"; // Apple signature blue
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Designed for Innovation",
    title: "Pro. Beyond.",
    subtitle: "Experience state-of-the-art tech engineered with premium materials and unmatched performance.",
    primaryCta: "Buy Now",
    secondaryCta: "Learn More",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7] text-[#1d1d1f]" style={{ fontFamily: T.fontFamily || "Inter, -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Promo banner */}
      <div className="w-full bg-[#1d1d1f] py-3 text-center text-xs text-white/90 font-medium px-4">
        <span>Get free delivery and interest-free installments on all orders. </span>
        <button onClick={(e) => navigateTo("shop", e)} className="text-[#2997ff] hover:underline inline-flex items-center gap-0.5 ml-1">
          Shop now <MdArrowForward className="text-[10px]" />
        </button>
      </div>

      {/* Apple-style Blur Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-sm" 
          : "bg-[#f5f5f7]/90 backdrop-blur-sm border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-gray-200 shadow-sm" size="h-9 w-9" />
            <div>
              <p className="text-base font-semibold tracking-tight text-[#1d1d1f]">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-gray-500 tracking-wider font-medium uppercase">{p.storeInfo?.businessCategory || "Premium Electronics"}</p>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop"],
              ["details", "About"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-normal tracking-wide transition-colors duration-200 hover:text-black ${
                  activePage === page || (page === "shop" && activePage === "shop") 
                    ? "text-[#1d1d1f] font-semibold" 
                    : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search, Wishlist, Cart */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-gray-200/50 text-[#1d1d1f] transition-colors" title="Wishlist">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#ff3b30] shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-gray-200/50 text-[#1d1d1f]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#f5f5f7] border-b border-gray-200 shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop"],
              ["details", "About Us"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-sm font-medium border-b border-gray-200 hover:text-black ${
                  activePage === page ? "text-black font-semibold" : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content Area */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Apple Centered Hero Product Banner */}
            <section className="w-full px-6 pt-8 pb-12 text-center bg-white flex flex-col items-center border-b border-gray-200/40">
              <div className="max-w-3xl flex flex-col items-center">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-[#1d1d1f] leading-none mb-4">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-lg md:text-xl text-gray-500 max-w-xl font-normal leading-relaxed mb-6">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="flex gap-4 mb-8">
                  <button 
                    onClick={() => navigateTo("shop")}
                    className="px-6 py-2.5 rounded-full text-sm font-medium text-white hover:opacity-90 active:scale-98 transition-all shadow-sm"
                    style={{ backgroundColor: buttonColor }}
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                  <button 
                    onClick={() => {
                      p.setActiveTab("details");
                      navigateTo("about");
                    }}
                    className="text-[#0066cc] hover:underline text-sm font-medium inline-flex items-center gap-1"
                  >
                    <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText> <MdArrowForward />
                  </button>
                </div>
              </div>

              {/* Large Product Hero Visual */}
              <div className="w-full max-w-5xl rounded-3xl overflow-hidden mt-2 bg-[#f5f5f7] border border-gray-200/50 shadow-[0_20px_50px_rgba(0,0,0,0.05)]">
                <HeroImage p={p} themeId="apple" className="h-[380px] md:h-[500px]">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                </HeroImage>
              </div>
            </section>

            {/* Apple Tech-spec / Benefits Spotlight */}
            <section className="max-w-6xl mx-auto px-6 py-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Supercharged engineering.</h2>
                <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">Discover the incredible hardware features integrated into our premium catalog.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Bionic Core M-SoC",
                    desc: "Engineered on a state-of-the-art lithography processor for hyper-efficient CPU and GPU capabilities.",
                    metric: "3nm",
                    sub: "Architecture"
                  },
                  {
                    title: "Super Retina XDR",
                    desc: "OLED displays that reach up to 2000 nits peak brightness for incredible HDR contrast and vivid clarity.",
                    metric: "2000 nits",
                    sub: "Brightness"
                  },
                  {
                    title: "All-Day Power",
                    desc: "Solid-state cell battery integration ensures power that lasts through your complete work day and beyond.",
                    metric: "24 hrs",
                    sub: "Battery Life"
                  }
                ].map((spec, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-3xl p-8 flex flex-col justify-between hover:shadow-lg transition-shadow duration-300 text-left">
                    <div>
                      <span className="text-[10px] tracking-widest font-bold text-gray-400 uppercase">SPECIFICATION</span>
                      <h4 className="text-xl font-bold mt-1 text-[#1d1d1f]">{spec.title}</h4>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">{spec.desc}</p>
                    </div>
                    <div className="mt-8 border-t border-gray-100 pt-4 flex items-baseline gap-2">
                      <span className="text-3xl font-bold tracking-tight text-[#1d1d1f]">{spec.metric}</span>
                      <span className="text-xs font-semibold text-gray-400">{spec.sub}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Value Badges */}
            <section className="bg-white border-y border-gray-200/50 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Free Next-Day Delivery", icon: <MdOutlineLocalShipping className="text-2xl text-gray-700" />, desc: "Fast shipping on all catalog items" },
                  { title: "Extended Store Warranty", icon: <MdOutlineSecurity className="text-2xl text-gray-700" />, desc: "Hassle-free coverage for peace of mind" },
                  { title: "Genuine Brand Guarantee", icon: <MdOutlineVerified className="text-2xl text-gray-700" />, desc: "100% verified products & accessories" },
                  { title: "Flexible Support", icon: <MdDevices className="text-2xl text-gray-700" />, desc: "Expert technical assistance anytime" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4">
                    <div className="p-3 rounded-full bg-gray-100 mb-3">{b.icon}</div>
                    <p className="text-xs font-bold text-[#1d1d1f]">{b.title}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">STORE CATALOG</span>
              <h3 className="text-3xl font-bold text-[#1d1d1f] mt-1">Explore Products</h3>
            </div>

            {/* Filter controls */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-xs font-medium text-gray-700 transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-full border border-gray-200 bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff" } : { color: "#555" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
